/**
 * Locale code enum for supported locales in the application
 */

import { LocaleCode } from './enums';

/**
 * Interface for locale display options
 */
export interface LocaleOption {
  key: LocaleCode;
  label: string;
  nativeName?: string;
  direction?: 'ltr' | 'rtl';
}

/**
 * Default locale for the application
 */
export const DEFAULT_LOCALE = LocaleCode.ENGLISH_US;

/**
 * Every locale in {@link LocaleCode}, with its display information. A locale
 * missing here cannot be shown or selected anywhere in the portal, so the list
 * must stay complete; `locale.test.ts` enforces that.
 */
export const LOCALE_OPTIONS: LocaleOption[] = [
  { key: LocaleCode.CZECH, label: 'Czech', nativeName: 'Čeština' },
  {
    key: LocaleCode.DUTCH_BELGIUM,
    label: 'Dutch (Belgium)',
    nativeName: 'Nederlands (België)',
  },
  { key: LocaleCode.ENGLISH_US, label: 'English (US)' },
  { key: LocaleCode.ENGLISH_GB, label: 'English (UK)' },
  { key: LocaleCode.ENGLISH_CA, label: 'English (Canada)' },
  { key: LocaleCode.ENGLISH_AE, label: 'English (UAE)' },
  { key: LocaleCode.ENGLISH_SA, label: 'English (Saudi Arabia)' },
  {
    key: LocaleCode.ARABIC_SAUDI_ARABIA,
    label: 'Arabic (Saudi Arabia)',
    nativeName: 'العربية',
    direction: 'rtl',
  },
  {
    key: LocaleCode.ARABIC_UAE,
    label: 'Arabic (UAE)',
    nativeName: 'العربية',
    direction: 'rtl',
  },
  { key: LocaleCode.FRENCH, label: 'French', nativeName: 'Français' },
  { key: LocaleCode.GERMAN, label: 'German', nativeName: 'Deutsch' },
  { key: LocaleCode.ITALIAN, label: 'Italian', nativeName: 'Italiano' },
  {
    key: LocaleCode.PORTUGUESE_BRAZIL,
    label: 'Portuguese (Brazil)',
    nativeName: 'Português (Brasil)',
  },
  { key: LocaleCode.SPANISH, label: 'Spanish', nativeName: 'Español' },
  { key: LocaleCode.RUSSIAN, label: 'Russian', nativeName: 'Русский' },
  { key: LocaleCode.TURKISH, label: 'Turkish', nativeName: 'Türkçe' },
];

/**
 * Helper function to get locale display information
 */
export function getLocaleInfo(localeCode: LocaleCode): LocaleOption {
  const locale = LOCALE_OPTIONS.find((option) => option.key === localeCode);
  if (!locale) {
    throw new Error(`Unsupported locale code: ${localeCode}`);
  }
  return locale;
}

/**
 * Helper function to validate locale code
 */
export function isValidLocaleCode(code: string): code is LocaleCode {
  return Object.values(LocaleCode).includes(code as LocaleCode);
}

/**
 * Helper function to get locale direction (ltr or rtl)
 */
export function getLocaleDirection(localeCode: LocaleCode): 'ltr' | 'rtl' {
  const locale = LOCALE_OPTIONS.find((option) => option.key === localeCode);
  return locale?.direction || 'ltr';
}
